/**
 * TanStack Query hooks for Room resource.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export interface Room {
  id: string;
  branchId: string;
  branchName?: string;
  name: string;
  capacity: number;
  isActive: boolean;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListRoomsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  branchId?: string;
  isActive?: boolean;
}

export const roomKeys = {
  all: ['rooms'] as const,
  list: (params?: ListRoomsParams) => ['rooms', 'list', params] as const,
  detail: (id: string) => ['rooms', 'detail', id] as const,
};

export function useRooms(params?: ListRoomsParams) {
  return useQuery({
    queryKey: roomKeys.list(params),
    queryFn: () =>
      api.getPage<Room>('/api/rooms', params as Record<string, string>),
  });
}

export function useRoom(id: string) {
  return useQuery({
    queryKey: roomKeys.detail(id),
    queryFn: () => api.get<Room>(`/api/rooms/${id}`),
    enabled: !!id,
  });
}

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<Room>('/api/rooms', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.all });
    },
  });
}

export function useUpdateRoom(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<Room>(`/api/rooms/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.all });
    },
  });
}

export function useDeleteRoom(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(`/api/rooms/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: roomKeys.all });
    },
  });
}
